import { Alert, Button, InputNumber, Table, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getRegionalPricing,
  parseInternationalPricingError,
  updateRegionalPricing,
} from "../../services/internationalPricingApi";
import {
  RegionalPricing,
  RegionalPricingChanges,
} from "../../shared/dtos/internationalPricingDTO";
import {
  getRegionalChanges,
  validateRegionalPricing,
} from "./utils";

const { Text } = Typography;

const numericColumns: Array<{
  title: string;
  field: keyof RegionalPricingChanges;
}> = [
  { title: "KM mín", field: "kilometerMinMultiplier" },
  { title: "KM máx", field: "kilometerMaxMultiplier" },
  { title: "Tarifa mín", field: "fareMinMultiplier" },
  { title: "Tarifa máx", field: "fareMaxMultiplier" },
  { title: "Ciudad", field: "cityMultiplier" },
  { title: "Cobro mín", field: "minimumChargeMultiplier" },
  { title: "Piso USD", field: "usdFloor" },
];

const RegionalFactorsTab = () => {
  const [rows, setRows] = useState<RegionalPricing[]>([]);
  const rowsRef = useRef<RegionalPricing[]>([]);
  const originals = useRef(new Map<string, RegionalPricing>());
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string>();
  const [error, setError] = useState<string>();
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await getRegionalPricing();
      const sorted = [...data].sort((a, b) => a.sortOrder - b.sortOrder);
      setRows(sorted);
      rowsRef.current = sorted;
      originals.current = new Map(
        sorted.map((row) => [row.regionKey, { ...row }]),
      );
    } catch (loadError) {
      setError(parseInternationalPricingError(loadError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const updateValue = (
    regionKey: string,
    field: keyof RegionalPricingChanges,
    value: number | null,
  ) => {
    if (value == null) return;
    setRows((current) => {
      const next = current.map((row) =>
        row.regionKey === regionKey ? { ...row, [field]: value } : row,
      );
      rowsRef.current = next;
      return next;
    });
    setRowErrors((current) => {
      const next = { ...current };
      delete next[regionKey];
      return next;
    });
  };

  const saveRow = async (regionKey: string) => {
    const row = rowsRef.current.find((item) => item.regionKey === regionKey);
    if (!row) return;
    const original = originals.current.get(row.regionKey);
    if (!original || savingKey === row.regionKey) return;

    const validationError = validateRegionalPricing(row);
    if (validationError) {
      setRowErrors((current) => ({
        ...current,
        [row.regionKey]: validationError,
      }));
      return;
    }

    const changes = getRegionalChanges(row, original);
    if (!Object.keys(changes).length) return;

    setSavingKey(row.regionKey);
    try {
      const updated = await updateRegionalPricing(row.regionKey, changes);
      setRows((current) =>
        current.map((item) =>
          item.regionKey === row.regionKey ? updated : item,
        ),
      );
      rowsRef.current = rowsRef.current.map((item) =>
        item.regionKey === row.regionKey ? updated : item,
      );
      originals.current.set(row.regionKey, { ...updated });
      setRowErrors((current) => {
        const next = { ...current };
        delete next[row.regionKey];
        return next;
      });
    } catch (saveError) {
      const parsed = parseInternationalPricingError(saveError);
      setRowErrors((current) => ({
        ...current,
        [row.regionKey]: parsed.message,
      }));
    } finally {
      setSavingKey(undefined);
    }
  };

  const columns: ColumnsType<RegionalPricing> = [
    {
      title: "Región",
      dataIndex: "label",
      fixed: "left",
      width: 160,
      render: (label: string, row) => (
        <div>
          <Tag color="blue">{label}</Tag>
          {rowErrors[row.regionKey] && (
            <Text type="danger" className="international-pricing-row-error">
              {rowErrors[row.regionKey]}
            </Text>
          )}
        </div>
      ),
    },
    ...numericColumns.map(({ title, field }) => ({
      title,
      dataIndex: field,
      width: 125,
      render: (_: number, row: RegionalPricing) => (
        <InputNumber
          min={0}
          value={row[field]}
          controls={false}
          status={rowErrors[row.regionKey] ? "error" : undefined}
          disabled={savingKey === row.regionKey}
          onChange={(value) => updateValue(row.regionKey, field, value)}
          onBlur={() => saveRow(row.regionKey)}
          onPressEnter={(event) => event.currentTarget.blur()}
        />
      ),
    })),
  ];

  return (
    <div className="international-pricing-tab">
      <Alert
        type="info"
        showIcon
        message="Cada región aplica multiplicadores base a las tarifas de sus ciudades. Los cambios se guardan al salir del campo o presionar Enter."
      />
      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          action={
            <Button type="link" size="small" onClick={loadRows}>
              Reintentar
            </Button>
          }
        />
      )}
      <Table
        rowKey="regionKey"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        scroll={{ x: 1050 }}
        size="middle"
      />
    </div>
  );
};

export default RegionalFactorsTab;
